<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import DatePicker from 'vue-datepicker-next';

const emit = defineEmits(['change']);

DatePicker.locale('pt-br', {
  formatLocale: {
    months: [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro',
    ],
    monthsShort: [
      'Jan',
      'Fev',
      'Mar',
      'Abr',
      'Mai',
      'Jun',
      'Jul',
      'Ago',
      'Set',
      'Out',
      'Nov',
      'Dez',
    ],
    weekdays: [
      'Domingo',
      'Segunda-feira',
      'Terça-feira',
      'Quarta-feira',
      'Quinta-feira',
      'Sexta-feira',
      'Sábado',
    ],
    weekdaysShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
    weekdaysMin: ['Do', '2ª', '3ª', '4ª', '5ª', '6ª', 'Sá'],
    firstDayOfWeek: 0,
    firstWeekContainsDate: 6,
  },
  yearFormat: 'YYYY',
  monthFormat: 'MMM',
  monthBeforeYear: true,
});

DatePicker.locale('es', {
  formatLocale: {
    months: [
      'enero',
      'febrero',
      'marzo',
      'abril',
      'mayo',
      'junio',
      'julio',
      'agosto',
      'septiembre',
      'octubre',
      'noviembre',
      'diciembre',
    ],
    monthsShort: [
      'ene',
      'feb',
      'mar',
      'abr',
      'may',
      'jun',
      'jul',
      'ago',
      'sep',
      'oct',
      'nov',
      'dic',
    ],
    weekdays: [
      'domingo',
      'lunes',
      'martes',
      'miércoles',
      'jueves',
      'viernes',
      'sábado',
    ],
    weekdaysShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    weekdaysMin: ['do', 'lu', 'ma', 'mi', 'ju', 'vi', 'sá'],
    firstDayOfWeek: 1,
    firstWeekContainsDate: 4,
  },
  yearFormat: 'YYYY',
  monthFormat: 'MMM',
  monthBeforeYear: true,
});
import {
  getUnixTime,
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  endOfMonth,
} from 'date-fns';

const { t, locale } = useI18n();

const PRESETS = [
  { key: 'today', labelKey: 'PAINEL.PERIOD.TODAY' },
  { key: '7d', labelKey: 'PAINEL.PERIOD.LAST_7_DAYS' },
  { key: '30d', labelKey: 'PAINEL.PERIOD.LAST_30_DAYS' },
  { key: 'month', labelKey: 'PAINEL.PERIOD.THIS_MONTH' },
  { key: 'custom', labelKey: 'PAINEL.PERIOD.CUSTOM' },
];

const selected = ref('7d');
const customRange = ref([]);

// Map Chatwoot locale (pt_BR) to vue-datepicker-next lang (pt-br)
const pickerLang = computed(() => locale.value.replace('_', '-').toLowerCase());

const getRangeForKey = key => {
  const now = new Date();
  if (key === 'today')
    return {
      since: getUnixTime(startOfDay(now)),
      until: getUnixTime(endOfDay(now)),
    };
  if (key === '7d')
    return {
      since: getUnixTime(startOfDay(subDays(now, 7))),
      until: getUnixTime(endOfDay(now)),
    };
  if (key === '30d')
    return {
      since: getUnixTime(startOfDay(subDays(now, 30))),
      until: getUnixTime(endOfDay(now)),
    };
  if (key === 'month')
    return {
      since: getUnixTime(startOfMonth(now)),
      until: getUnixTime(endOfMonth(now)),
    };
  return null;
};

const select = key => {
  selected.value = key;
  const range = getRangeForKey(key);
  if (range) emit('change', range);
};

const onDateRangeChanged = value => {
  if (!value || !value[0] || !value[1]) return;
  customRange.value = value;
  emit('change', {
    since: getUnixTime(startOfDay(value[0])),
    until: getUnixTime(endOfDay(value[1])),
  });
};

onMounted(() => {
  emit('change', getRangeForKey('7d'));
});
</script>

<template>
  <div class="flex items-center gap-2 flex-wrap">
    <div class="flex gap-1">
      <button
        v-for="preset in PRESETS"
        :key="preset.key"
        class="px-3 py-1 text-sm rounded-md border transition-colors"
        :class="
          selected === preset.key
            ? 'bg-n-blue-8 text-white border-n-blue-8'
            : 'border-n-slate-5 text-n-slate-11 hover:bg-n-alpha-2'
        "
        @click="select(preset.key)"
      >
        {{ t(preset.labelKey) }}
      </button>
    </div>
    <div v-if="selected === 'custom'" class="date-picker small no-margin">
      <DatePicker
        range
        confirm
        :clearable="false"
        :editable="false"
        :confirm-text="t('PAINEL.PERIOD.CONFIRM')"
        :placeholder="t('PAINEL.PERIOD.CUSTOM_PLACEHOLDER')"
        :lang="pickerLang"
        :value="customRange"
        @change="onDateRangeChanged"
      />
    </div>
  </div>
</template>
